import { api } from './api';
import { isIosDevice, isRunningAsInstalledPwa } from './pwaInstallService';

const SUBSCRIPTION_STORAGE_KEY = 'notification_subscription';

export const NOTIFICATION_ELIGIBLE_USER_LEVELS = Object.freeze(['staff_artist', 'guest_artist']);

export const NOTIFICATION_DEVICE_STATUS = Object.freeze({
  CHECKING: 'checking',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  UNAVAILABLE: 'unavailable'
});

export const NOTIFICATION_DEVICE_STATUS_EVENT = 'notification-device-status-change';

export const SCHEDULE_NOTIFICATION_STATUS = Object.freeze({
  CHECKING: 'checking',
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  UNAVAILABLE: 'unavailable'
});

export const SCHEDULE_NOTIFICATION_STATUS_EVENT = 'schedule-notification-status-change';

const serverStatusByUserId = new Map();
const scheduleStatusByUserId = new Map();
const notificationPreferencesRequestByUserId = new Map();

export function isNotificationEligibleUserLevel(level) {
  return NOTIFICATION_ELIGIBLE_USER_LEVELS.includes(level);
}

export function getNotificationRuntimeState() {
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotifications = 'Notification' in window;

  return {
    supported: hasServiceWorker && hasPushManager && hasNotifications,
    isIos: isIosDevice(),
    isStandalone: isRunningAsInstalledPwa(),
    permission: hasNotifications ? Notification.permission : 'unsupported'
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((character) => character.charCodeAt(0)));
}

function getStoredSubscription(userId) {
  try {
    const value = JSON.parse(localStorage.getItem(SUBSCRIPTION_STORAGE_KEY));
    return Number(value?.userId) === Number(userId) && value?.id ? value : null;
  } catch (error) {
    return null;
  }
}

function saveStoredSubscription(userId, id) {
  localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify({ userId, id }));
}

function clearStoredSubscription() {
  localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
}

function createDeviceStatus(state, reason, runtime, overrides = {}) {
  return {
    state,
    reason,
    runtime,
    browserSubscribed: false,
    serverConnected: false,
    ...overrides
  };
}

function createScheduleStatus(state, reason, enabled = null) {
  return { state, reason, enabled };
}

export function getInitialNotificationDeviceStatus(userId, userLevel) {
  const runtime = getNotificationRuntimeState();

  if (!userId) {
    return createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'signed_out', runtime);
  }
  if (!isNotificationEligibleUserLevel(userLevel)) {
    return createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'ineligible_user', runtime);
  }
  if (!runtime.supported) {
    return createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'unsupported', runtime);
  }
  if (runtime.isIos && !runtime.isStandalone) {
    return createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'ios_install_required', runtime);
  }
  if (runtime.permission !== 'granted') {
    return createDeviceStatus(
      NOTIFICATION_DEVICE_STATUS.INACTIVE,
      runtime.permission === 'denied' ? 'permission_denied' : 'permission_required',
      runtime
    );
  }

  return createDeviceStatus(NOTIFICATION_DEVICE_STATUS.CHECKING, 'initializing', runtime);
}

export function getInitialScheduleNotificationStatus(userId, userLevel) {
  if (!userId || !isNotificationEligibleUserLevel(userLevel)) {
    return createScheduleStatus(SCHEDULE_NOTIFICATION_STATUS.UNAVAILABLE, 'ineligible_user');
  }

  return scheduleStatusByUserId.get(Number(userId))
    || createScheduleStatus(SCHEDULE_NOTIFICATION_STATUS.CHECKING, 'initializing');
}

function publishNotificationDeviceStatus(userId, status) {
  const numericUserId = Number(userId);
  if (Number.isFinite(numericUserId)) {
    serverStatusByUserId.set(numericUserId, status);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_DEVICE_STATUS_EVENT, {
      detail: { userId: numericUserId, status }
    }));
  }

  return status;
}

function publishScheduleNotificationStatus(userId, enabled) {
  const numericUserId = Number(userId);
  const status = createScheduleStatus(
    enabled ? SCHEDULE_NOTIFICATION_STATUS.ENABLED : SCHEDULE_NOTIFICATION_STATUS.DISABLED,
    enabled ? 'schedule_notifications_enabled' : 'schedule_notifications_disabled',
    enabled
  );

  if (Number.isFinite(numericUserId)) {
    scheduleStatusByUserId.set(numericUserId, status);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SCHEDULE_NOTIFICATION_STATUS_EVENT, {
      detail: { userId: numericUserId, status }
    }));
  }

  return status;
}

function publishTransientCheckFailure(userId, reason, overrides = {}) {
  const numericUserId = Number(userId);
  const lastKnownStatus = serverStatusByUserId.get(numericUserId);
  if (lastKnownStatus?.state === NOTIFICATION_DEVICE_STATUS.ACTIVE) {
    return publishNotificationDeviceStatus(userId, lastKnownStatus);
  }

  return publishNotificationDeviceStatus(
    userId,
    createDeviceStatus(
      NOTIFICATION_DEVICE_STATUS.CHECKING,
      reason,
      getNotificationRuntimeState(),
      {
        browserSubscribed: Boolean(lastKnownStatus?.browserSubscribed),
        serverConnected: false,
        ...overrides
      }
    )
  );
}

async function getRegistration() {
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  return navigator.serviceWorker.ready;
}

async function registerSubscriptionWithServer(userId, subscription) {
  const response = await api.post('/notifications/subscriptions', {
    subscription: subscription.toJSON()
  });
  saveStoredSubscription(userId, response.subscription.id);
  publishNotificationDeviceStatus(
    userId,
    createDeviceStatus(
      NOTIFICATION_DEVICE_STATUS.ACTIVE,
      'connected',
      getNotificationRuntimeState(),
      {
        browserSubscribed: true,
        serverConnected: true,
        subscriptionId: response.subscription.id
      }
    )
  );
  return response.subscription;
}

async function createBrowserSubscription(registration, vapidPublicKey) {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
}

async function registerSubscriptionWithConflictRecovery({ userId, registration, subscription, vapidPublicKey }) {
  try {
    return await registerSubscriptionWithServer(userId, subscription);
  } catch (error) {
    if (error.status !== 409 || error.data?.code !== 'PUSH_SUBSCRIPTION_ACCOUNT_CONFLICT') {
      throw error;
    }

    await subscription.unsubscribe();
    clearStoredSubscription();
    const newSubscription = await createBrowserSubscription(registration, vapidPublicKey);
    return registerSubscriptionWithServer(userId, newSubscription);
  }
}

export async function getCurrentDeviceNotificationStatus(userId, userLevel) {
  const initialStatus = getInitialNotificationDeviceStatus(userId, userLevel);
  if (initialStatus.state !== NOTIFICATION_DEVICE_STATUS.CHECKING) {
    return initialStatus;
  }

  try {
    const registration = await getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      clearStoredSubscription();
      serverStatusByUserId.delete(Number(userId));
      return createDeviceStatus(
        NOTIFICATION_DEVICE_STATUS.INACTIVE,
        'browser_subscription_missing',
        initialStatus.runtime
      );
    }

    const serverStatus = serverStatusByUserId.get(Number(userId));
    if (serverStatus?.state === NOTIFICATION_DEVICE_STATUS.UNAVAILABLE) {
      return {
        ...serverStatus,
        runtime: initialStatus.runtime,
        browserSubscribed: true
      };
    }

    const stored = getStoredSubscription(userId);
    if (!stored) {
      return createDeviceStatus(
        NOTIFICATION_DEVICE_STATUS.CHECKING,
        'server_connection_unconfirmed',
        initialStatus.runtime,
        { browserSubscribed: true }
      );
    }

    const isVerifiedActive = serverStatus?.state === NOTIFICATION_DEVICE_STATUS.ACTIVE
      && serverStatus.subscriptionId === stored.id;
    if (isVerifiedActive) {
      return {
        ...serverStatus,
        runtime: initialStatus.runtime,
        browserSubscribed: true
      };
    }

    return createDeviceStatus(
      NOTIFICATION_DEVICE_STATUS.CHECKING,
      'server_connection_unconfirmed',
      initialStatus.runtime,
      { browserSubscribed: true }
    );
  } catch (error) {
    const lastKnownStatus = serverStatusByUserId.get(Number(userId));
    if (lastKnownStatus?.state === NOTIFICATION_DEVICE_STATUS.ACTIVE) {
      return { ...lastKnownStatus, runtime: initialStatus.runtime };
    }
    return createDeviceStatus(
      NOTIFICATION_DEVICE_STATUS.CHECKING,
      'status_check_failed',
      initialStatus.runtime
    );
  }
}

export async function getCurrentScheduleNotificationStatus(userId, userLevel) {
  const initialStatus = getInitialScheduleNotificationStatus(userId, userLevel);
  if (initialStatus.state === SCHEDULE_NOTIFICATION_STATUS.UNAVAILABLE) {
    return initialStatus;
  }

  try {
    const preferences = await getNotificationPreferences(userId);
    return scheduleStatusByUserId.get(Number(userId))
      || createScheduleStatus(
        preferences.enabled ? SCHEDULE_NOTIFICATION_STATUS.ENABLED : SCHEDULE_NOTIFICATION_STATUS.DISABLED,
        preferences.enabled ? 'schedule_notifications_enabled' : 'schedule_notifications_disabled',
        Boolean(preferences.enabled)
      );
  } catch (error) {
    return scheduleStatusByUserId.get(Number(userId))
      || createScheduleStatus(SCHEDULE_NOTIFICATION_STATUS.CHECKING, 'load_failed');
  }
}

export async function getNotificationConfiguration() {
  const response = await api.get('/notifications/configuration');
  return response.configuration;
}

export async function getNotificationPreferences(userId) {
  const numericUserId = Number(userId);
  const loadPreferences = async () => {
    const response = await api.get('/notifications/preferences');
    if (Number.isFinite(numericUserId)) {
      publishScheduleNotificationStatus(userId, Boolean(response.preferences.enabled));
    }
    return response.preferences;
  };

  if (!Number.isFinite(numericUserId)) {
    return loadPreferences();
  }
  if (notificationPreferencesRequestByUserId.has(numericUserId)) {
    return notificationPreferencesRequestByUserId.get(numericUserId);
  }

  const request = loadPreferences().finally(() => {
    notificationPreferencesRequestByUserId.delete(numericUserId);
  });
  notificationPreferencesRequestByUserId.set(numericUserId, request);
  return request;
}

export async function saveNotificationPreferences(preferences, userId) {
  try {
    const response = await api.put('/notifications/preferences', preferences);
    if (Number.isFinite(Number(userId))) {
      publishScheduleNotificationStatus(userId, Boolean(response.preferences.enabled));
    }
    return response.preferences;
  } catch (error) {
    if (Number.isFinite(Number(userId)) && typeof error.data?.preferences?.enabled === 'boolean') {
      publishScheduleNotificationStatus(userId, error.data.preferences.enabled);
    }
    throw error;
  }
}

export async function previewNotifications(preferences, language) {
  const response = await api.post('/notifications/preview', { ...preferences, language });
  return response.previews;
}

export async function subscribeCurrentDevice(userId) {
  const runtime = getNotificationRuntimeState();
  if (!runtime.supported) {
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'unsupported', runtime)
    );
    throw new Error('PUSH_UNSUPPORTED');
  }
  if (runtime.isIos && !runtime.isStandalone) {
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'ios_install_required', runtime)
    );
    throw new Error('IOS_INSTALL_REQUIRED');
  }

  let configuration;
  try {
    configuration = await getNotificationConfiguration();
  } catch (error) {
    publishTransientCheckFailure(userId, 'configuration_check_failed');
    throw error;
  }
  if (!configuration.enabled || !configuration.vapid_public_key) {
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'server_disabled', runtime)
    );
    throw new Error('WEB_PUSH_DISABLED');
  }
  if (!configuration.eligible_user) {
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(NOTIFICATION_DEVICE_STATUS.UNAVAILABLE, 'ineligible_user', runtime)
    );
    throw new Error('NOTIFICATION_USER_NOT_ELIGIBLE');
  }

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();
  if (permission !== 'granted') {
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(
        NOTIFICATION_DEVICE_STATUS.INACTIVE,
        permission === 'denied' ? 'permission_denied' : 'permission_required',
        getNotificationRuntimeState()
      )
    );
    throw new Error(permission === 'denied' ? 'PUSH_PERMISSION_DENIED' : 'PUSH_PERMISSION_REQUIRED');
  }

  const registration = await getRegistration();
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await createBrowserSubscription(registration, configuration.vapid_public_key);
  }

  try {
    return await registerSubscriptionWithConflictRecovery({
      userId,
      registration,
      subscription,
      vapidPublicKey: configuration.vapid_public_key
    });
  } catch (error) {
    publishTransientCheckFailure(userId, 'subscription_save_failed', { browserSubscribed: true });
    throw error;
  }
}

export async function reconcileCurrentSubscription(userId, userLevel) {
  const initialStatus = getInitialNotificationDeviceStatus(userId, userLevel);
  if (initialStatus.state !== NOTIFICATION_DEVICE_STATUS.CHECKING) {
    publishNotificationDeviceStatus(userId, initialStatus);
    return null;
  }

  let registration;
  let subscription;
  try {
    registration = await getRegistration();
    subscription = await registration.pushManager.getSubscription();
  } catch (error) {
    publishTransientCheckFailure(userId, 'browser_subscription_check_failed');
    throw error;
  }
  if (!subscription) {
    clearStoredSubscription();
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(
        NOTIFICATION_DEVICE_STATUS.INACTIVE,
        'browser_subscription_missing',
        initialStatus.runtime
      )
    );
    return null;
  }

  let configuration;
  try {
    configuration = await getNotificationConfiguration();
  } catch (error) {
    publishTransientCheckFailure(userId, 'configuration_check_failed', { browserSubscribed: true });
    throw error;
  }
  if (!configuration.enabled || !configuration.eligible_user) {
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(
        NOTIFICATION_DEVICE_STATUS.UNAVAILABLE,
        configuration.enabled ? 'ineligible_user' : 'server_disabled',
        initialStatus.runtime,
        { browserSubscribed: true }
      )
    );
    return null;
  }

  try {
    return await registerSubscriptionWithConflictRecovery({
      userId,
      registration,
      subscription,
      vapidPublicKey: configuration.vapid_public_key
    });
  } catch (error) {
    publishTransientCheckFailure(userId, 'subscription_reconcile_failed', { browserSubscribed: true });
    throw error;
  }
}

export async function unsubscribeCurrentDevice(userId) {
  const stored = getStoredSubscription(userId);

  try {
    if (stored?.id) {
      await api.delete(`/notifications/subscriptions/${stored.id}`);
    }
  } catch (error) {
    if (error.status !== 404) {
      throw error;
    }
  } finally {
    if ('serviceWorker' in navigator) {
      const registration = await getRegistration().catch(() => null);
      const subscription = await registration?.pushManager.getSubscription().catch(() => null);
      await subscription?.unsubscribe().catch(() => false);
    }
    clearStoredSubscription();
    publishNotificationDeviceStatus(
      userId,
      createDeviceStatus(
        NOTIFICATION_DEVICE_STATUS.INACTIVE,
        'disconnected',
        getNotificationRuntimeState()
      )
    );
  }
}

export async function sendTestNotification(userId) {
  const stored = getStoredSubscription(userId);
  if (!stored?.id) {
    throw new Error('PUSH_SUBSCRIPTION_REQUIRED');
  }
  return api.post('/notifications/test', { subscriptionId: stored.id });
}

export function hasStoredNotificationSubscription(userId) {
  return Boolean(getStoredSubscription(userId));
}
